/**
 * BespokeBridge – Event Proposals API
 * Proposals live in the root `events` collection and are distinguished from
 * publishable events by their status prefix: PROPOSAL_PENDING | PROPOSAL_APPROVED | PROPOSAL_DECLINED.
 * A `record_type: 'proposal'` field is also set so queries can filter without
 * scanning all events.
 *
 * On approval, the proposal document is converted in-place to a DRAFT event —
 * no second document is created.
 */
import {
  collection, doc, getDoc, addDoc, getDocs, updateDoc,
  query, where, serverTimestamp, deleteField,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { createNotification, NOTIFICATION_TYPES } from './notificationsApi';

const eventsCol = () => collection(db, 'events');
const eventDoc  = (id) => doc(db, 'events', id);
const now       = () => serverTimestamp();

// ─── Submit ────────────────────────────────────────────────────────────────────

/**
 * Volunteer submits an event creation proposal.
 * Creates a document in the `events` collection with status PROPOSAL_PENDING.
 */
export const submitEventProposal = async (orgId, volunteerId, volunteerName, proposalData) => {
  const ref = await addDoc(eventsCol(), {
    ...proposalData,
    orgId,
    submitted_by:      volunteerId,
    submitted_by_name: volunteerName,
    status:            'PROPOSAL_PENDING',
    record_type:       'proposal',
    submitted_at:      now(),
    updated_at:        now(),
  });

  await createNotification(
    orgId,
    'ADMIN',
    NOTIFICATION_TYPES.EVENT_PROPOSAL_NEW,
    'New Event Proposal',
    `${volunteerName} submitted a proposal: "${proposalData.title}"`,
    { proposal_id: ref.id, volunteer_id: volunteerId }
  );

  return ref.id;
};

// ─── Read ──────────────────────────────────────────────────────────────────────

/**
 * Get proposals for an org, optionally filtered by status.
 * status should be one of: 'PROPOSAL_PENDING' | 'PROPOSAL_APPROVED' | 'PROPOSAL_DECLINED' | null
 */
export const getEventProposals = async (orgId, status = null) => {
  const snap = await getDocs(
    query(eventsCol(), where('orgId', '==', orgId), where('record_type', '==', 'proposal'))
  );
  let proposals = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (status) proposals = proposals.filter(p => p.status === status);
  proposals.sort((a, b) => {
    const ta = a.submitted_at?.toDate ? a.submitted_at.toDate() : new Date(a.submitted_at || 0);
    const tb = b.submitted_at?.toDate ? b.submitted_at.toDate() : new Date(b.submitted_at || 0);
    return tb - ta;
  });
  return proposals;
};

/** Get proposals submitted by a specific volunteer */
export const getVolunteerProposals = async (orgId, volunteerId) => {
  const snap = await getDocs(
    query(
      eventsCol(),
      where('orgId', '==', orgId),
      where('submitted_by', '==', volunteerId),
      where('record_type', '==', 'proposal')
    )
  );
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const ta = a.submitted_at?.toDate ? a.submitted_at.toDate() : new Date(a.submitted_at || 0);
      const tb = b.submitted_at?.toDate ? b.submitted_at.toDate() : new Date(b.submitted_at || 0);
      return tb - ta;
    });
};

// ─── Moderate ─────────────────────────────────────────────────────────────────

/**
 * Admin approves a proposal.
 * Converts the proposal document in-place to a DRAFT event — the proposal IS
 * the event; no second document is created. Returns the eventId (same as proposalId).
 */
export const approveProposal = async (orgId, proposalId, adminId) => {
  const propSnap = await getDoc(eventDoc(proposalId));
  if (!propSnap.exists()) throw new Error('Proposal not found');
  const proposal = propSnap.data();

  await updateDoc(eventDoc(proposalId), {
    status:                 'DRAFT',
    record_type:            deleteField(),
    reviewed_by:            adminId,
    reviewed_at:            now(),
    updated_at:             now(),
    created_by:             proposal.submitted_by,
    created_at:             proposal.submitted_at,
    assigned_volunteers:    proposal.assigned_volunteers    || [],
    volunteer_signup_count: proposal.volunteer_signup_count || 0,
  });

  await createNotification(
    orgId,
    proposal.submitted_by,
    NOTIFICATION_TYPES.EVENT_PROPOSAL_APPROVED,
    'Your event proposal was approved!',
    `Your proposal "${proposal.title}" has been approved and a draft event has been created.`,
    { proposal_id: proposalId, event_id: proposalId }
  );

  return proposalId;
};

/**
 * Admin declines a proposal.
 */
export const declineProposal = async (orgId, proposalId, adminId, declineReason = '') => {
  const propSnap = await getDoc(eventDoc(proposalId));
  if (!propSnap.exists()) throw new Error('Proposal not found');
  const proposal = propSnap.data();

  await updateDoc(eventDoc(proposalId), {
    status:         'PROPOSAL_DECLINED',
    reviewed_by:    adminId,
    reviewed_at:    now(),
    decline_reason: declineReason,
    updated_at:     now(),
  });

  await createNotification(
    orgId,
    proposal.submitted_by,
    NOTIFICATION_TYPES.EVENT_PROPOSAL_DECLINED,
    'Your event proposal was not approved',
    declineReason
      ? `Your proposal "${proposal.title}" was declined: ${declineReason}`
      : `Your proposal "${proposal.title}" was not approved at this time.`,
    { proposal_id: proposalId }
  );
};
